import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  DocumentTextIcon,
  ChartBarIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import { getCompanyDetail } from "../api/companies";
import CompanyAvatar from "../components/CompanyAvatar";

const STATUS_COLORS = {
  COMPLETED: "bg-green-100 text-green-700",
  EXPIRED: "bg-amber-100 text-amber-700",
  INITIATED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-violet-100 text-violet-700",
  DECLINED: "bg-red-100 text-red-700",
  ARCHIVED: "bg-gray-100 text-gray-600",
};

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const CompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["company-detail", companyId],
    queryFn: () => getCompanyDetail(companyId).then((r) => r.data),
    enabled: !!companyId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data?.company) {
    return (
      <div className="space-y-6">
        <p className="text-red-500">
          Failed to load company.{" "}
          <button onClick={() => navigate("/dashboard/companies")} className="underline">
            Back to Companies
          </button>
        </p>
      </div>
    );
  }

  const { company, organization, documents, signRequestStats } = data;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate("/dashboard/companies")} className="hover:text-primary">
          Companies
        </button>
        <span>›</span>
        <span className="text-gray-900 font-medium">{company?.name || "Company"}</span>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <CompanyAvatar company={company} size="lg" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{company?.name || "—"}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{company?.phone || "—"}</p>
              <p className="text-sm text-gray-500">{company?.address || "—"}</p>
              {organization && (
                <button
                  onClick={() => navigate(`/dashboard/organizations/${organization._id}`)}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
                >
                  <BuildingOfficeIcon className="h-4 w-4" />
                  {organization.name}
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sign Request Statistics */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
            <ChartBarIcon className="w-5 h-5 text-violet-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Sign Request Statistics</h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{signRequestStats?.total ?? 0}</p>
              <p className="text-xs text-gray-500 mt-0.5">Total</p>
            </div>
            {Object.entries(signRequestStats || {})
              .filter(([k]) => k !== "total")
              .map(([status, count]) => (
                <div key={status} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{count ?? 0}</p>
                  <p className={`text-xs mt-0.5 px-2 py-0.5 rounded-full inline-block ${STATUS_COLORS[status] || "bg-gray-200 text-gray-700"}`}>
                    {status.replace(/_/g, " ")}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
            <DocumentTextIcon className="w-5 h-5 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Documents ({documents?.length ?? 0})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!documents?.length ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-gray-400">
                    No documents
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <tr key={doc._id} className="hover:bg-gray-50/80">
                    <td className="px-5 py-3 font-medium text-gray-900 truncate max-w-[200px]">{doc.fileName}</td>
                    <td className="px-5 py-3 text-gray-500">{doc.fileType || "—"}</td>
                    <td className="px-5 py-3 text-gray-500">{formatBytes(doc.fileSize)}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        doc.status === "signed" ? "bg-green-100 text-green-700" :
                        doc.status === "rejected" ? "bg-red-100 text-red-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>
                        {doc.status || "pending"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-400">
                      {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;
